// ============================================
// NRS.06 — Chat Disclosure Detection Service
// ============================================
// Refactored from: src/services/nurse/chatDisclosureDetector.ts
// Spec: hlx-nrs-06-chat-disclosure-detection.md (LOCKED)
// Contract: Keyword scanning, LateRevealEvent per PRO.09-C12
//           (confidence 0.5-0.6), session-scoped dedup,
//           SafetyDataProfile merge. Chat disclosures do NOT
//           flip DataCompletenessFlags (LOCKED contract).
// ============================================

import type {
  DisclosureType,
  DetectedDisclosure,
  KeywordMatch,
  LateRevealEvent,
  LateRevealExtractedData,
  DeduplicationState,
  SafetyDataMerge,
  ChatDisclosureMetrics,
} from '../types/chatDisclosureDetection';

// ── Constants ──

const DETECTION_MODEL_VERSION = 'nrs06-regex-v1.0';
const EVENT_SOURCE = 'b09_keyword_scan' as const;

/** Confidence range per PRO.09-C12 contract: 0.5-0.6 */
const CONFIDENCE_MIN = 0.5;
const CONFIDENCE_MAX = 0.6;

// ── Keyword Patterns ──

const MEDICATION_PATTERNS: KeywordMatch[] = [
  // ── Original patterns ──
  { term: 'metformin', pattern: /\bmetformin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'lisinopril', pattern: /\blisinopril\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'levothyroxine', pattern: /\blevothyroxine\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'atorvastatin', pattern: /\batorvastatin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'amlodipine', pattern: /\bamlodipine\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'omeprazole', pattern: /\bomeprazole\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'warfarin', pattern: /\bwarfarin\b/i, category: 'MEDICATION', weight: 0.95 },
  { term: 'coumadin', pattern: /\bcoumadin\b/i, category: 'MEDICATION', weight: 0.95 },
  { term: 'blood thinner', pattern: /\bblood\s+thinner/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'insulin', pattern: /\binsulin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'birth control', pattern: /\bbirth\s+control/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'antidepressant', pattern: /\bantidepressant/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'taking', pattern: /\b(?:i'?m|i am|i take|been taking|started taking)\s+\w+/i, category: 'MEDICATION', weight: 0.5 },
  { term: 'prescribed', pattern: /\bprescribed\b/i, category: 'MEDICATION', weight: 0.7 },
  { term: 'medication', pattern: /\bmy\s+(?:medication|meds?)\b/i, category: 'MEDICATION', weight: 0.6 },

  // ── Expanded: Real medications from Dr. C encounters ──
  { term: 'testosterone', pattern: /\btestosterone\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'berberine', pattern: /\bberberine\b/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'armour thyroid', pattern: /\barmou?r\s+thyroid\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'losartan', pattern: /\blosartan\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'celecoxib', pattern: /\bcelecoxib\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'metoprolol', pattern: /\bmetoprolol\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'sermorelin', pattern: /\bsermorelin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'prednisone', pattern: /\bprednisone\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'ocrevus', pattern: /\bocrevus\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'rosuvastatin', pattern: /\brosuvastatin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'finasteride', pattern: /\bfinasteride\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'semaglutide', pattern: /\bsemaglutide\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'lexapro', pattern: /\blexapro\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'cymbalta', pattern: /\bcymbalta\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'ativan', pattern: /\bativan\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'estrogen', pattern: /\bestrogen\b/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'progesterone', pattern: /\bprogesterone\b/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'amoxicillin', pattern: /\bamoxicillin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'statin', pattern: /\bstatin\b/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'beta blocker', pattern: /\bbeta[- ]?blocker/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'thyroid medication', pattern: /\bthyroid\s+(?:medication|med|medicine)\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'cholesterol medication', pattern: /\bcholesterol\s+(?:medication|med|medicine|lowering)\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'blood pressure medication', pattern: /\bblood\s+pressure\s+(?:medication|med|medicine)\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'hydroxychloroquine', pattern: /\bhydroxychloroquine\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'remicade', pattern: /\bremicade\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'eliquis', pattern: /\beliquis\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'valsartan', pattern: /\bvalsartan\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'gabapentin', pattern: /\bgabapentin\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'clomiphene', pattern: /\bclomiphene\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'arimidex', pattern: /\barimidex\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'allopurinol', pattern: /\ballopurinol\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'hydrochlorothiazide', pattern: /\bhydrochlorothiazide\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'tirzepatide', pattern: /\btirzepatide\b/i, category: 'MEDICATION', weight: 0.9 },
  { term: 'peptides', pattern: /\bpeptides?\b/i, category: 'MEDICATION', weight: 0.7 },

  // ── Expanded: Supplements from Dr. C encounters ──
  { term: 'vitamin d', pattern: /\bvitamin\s*d\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'iron supplement', pattern: /\biron\s+(?:supplement|pill|shot)/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'iron', pattern: /\biron\b/i, category: 'MEDICATION', weight: 0.6 },
  { term: 'vitamin c', pattern: /\bvitamin\s*c\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'magnesium', pattern: /\bmagnesium\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'dhea', pattern: /\bdhea\b/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'fish oil', pattern: /\bfish\s+oil\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'turmeric', pattern: /\bturmeric\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'curcumin', pattern: /\bcurcumin\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'coq10', pattern: /\bco\s*q\s*10\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'zinc', pattern: /\bzinc\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'probiotic', pattern: /\bprobiotic/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'melatonin', pattern: /\bmelatonin\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'omega-3', pattern: /\bomega[- ]?3\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'collagen', pattern: /\bcollagen\b/i, category: 'MEDICATION', weight: 0.75 },
  { term: 'multivitamin', pattern: /\bmultivitamin\b/i, category: 'MEDICATION', weight: 0.75 },
  { term: 'saw palmetto', pattern: /\bsaw\s+palmetto\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'glutathione', pattern: /\bglutathione\b/i, category: 'MEDICATION', weight: 0.85 },
  { term: 'red yeast rice', pattern: /\bred\s+yeast\s+rice\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'ashwagandha', pattern: /\bashwagandha\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'selenium', pattern: /\bselenium\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'vitamin b12', pattern: /\bvitamin\s*b\s*12\b|\bb12\b/i, category: 'MEDICATION', weight: 0.8 },
  { term: 'vitamin a', pattern: /\bvitamin\s*a\b/i, category: 'MEDICATION', weight: 0.75 },
  { term: 'vitamin pack', pattern: /\bvitamin\s+pack\b/i, category: 'MEDICATION', weight: 0.7 },
  { term: 'potassium', pattern: /\bpotassium\b/i, category: 'MEDICATION', weight: 0.75 },
  { term: 'calcium', pattern: /\bcalcium\b/i, category: 'MEDICATION', weight: 0.75 },
  { term: 'vitamin supplementation', pattern: /\bvitamin\s+supplementation\b/i, category: 'MEDICATION', weight: 0.7 },
  { term: 'vitamin regimen', pattern: /\bvitamin\s+regimen\b/i, category: 'MEDICATION', weight: 0.7 },
  { term: 'supplement', pattern: /\bsupplement(?:s|ation)?\b/i, category: 'MEDICATION', weight: 0.5 },
];

const ALLERGY_PATTERNS: KeywordMatch[] = [
  { term: 'allergic to', pattern: /\ballergic\s+to\b/i, category: 'ALLERGY', weight: 0.9 },
  { term: 'allergy', pattern: /\b(?:i have|i've got)\s+(?:an?\s+)?allerg/i, category: 'ALLERGY', weight: 0.85 },
  { term: 'react to', pattern: /\breact\s+to\b/i, category: 'ALLERGY', weight: 0.7 },
  { term: 'sensitive to', pattern: /\bsensitive\s+to\b/i, category: 'ALLERGY', weight: 0.6 },
  { term: 'can\'t take', pattern: /\bcan'?t\s+take\b/i, category: 'ALLERGY', weight: 0.65 },
  { term: 'intolerant', pattern: /\bintolerant\b/i, category: 'ALLERGY', weight: 0.7 },
  { term: 'anaphylaxis', pattern: /\banaphyla/i, category: 'ALLERGY', weight: 0.95 },
  { term: 'epipen', pattern: /\bepipen\b/i, category: 'ALLERGY', weight: 0.95 },
  { term: 'shellfish', pattern: /\bshellfish\b/i, category: 'ALLERGY', weight: 0.7 },
  { term: 'penicillin', pattern: /\bpenicillin\b/i, category: 'ALLERGY', weight: 0.8 },
];

const CONDITION_PATTERNS: KeywordMatch[] = [
  // ── Original patterns ──
  { term: 'diabetes', pattern: /\bdiabet/i, category: 'CONDITION', weight: 0.9 },
  { term: 'hypertension', pattern: /\bhypertension\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'high blood pressure', pattern: /\bhigh\s+blood\s+pressure/i, category: 'CONDITION', weight: 0.85 },
  { term: 'thyroid', pattern: /\bthyroid\s+(?:issue|problem|condition|disease|disorder)/i, category: 'CONDITION', weight: 0.85 },
  { term: 'hashimoto', pattern: /\bhashimoto/i, category: 'CONDITION', weight: 0.9 },
  { term: 'pcos', pattern: /\bpcos\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'autoimmune', pattern: /\bautoimmune\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'diagnosed with', pattern: /\bdiagnosed\s+with\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'liver disease', pattern: /\bliver\s+(?:disease|problem|condition)/i, category: 'CONDITION', weight: 0.85 },
  { term: 'kidney disease', pattern: /\bkidney\s+(?:disease|problem|condition|function)/i, category: 'CONDITION', weight: 0.85 },
  { term: 'cancer', pattern: /\bcancer\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'heart condition', pattern: /\bheart\s+(?:condition|disease|problem|failure|murmur)/i, category: 'CONDITION', weight: 0.9 },

  // ── Expanded: Real conditions from Dr. C encounters ──
  { term: 'anemia', pattern: /\banemia\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'cholesterol', pattern: /\b(?:high\s+)?cholesterol\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'hypercholesterolemia', pattern: /\bhypercholesterolemia\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'atrial fibrillation', pattern: /\batrial\s+fibrillation\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'hemochromatosis', pattern: /\bhemochromatosis\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'gout', pattern: /\bgout\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'multiple sclerosis', pattern: /\bmultiple\s+sclerosis\b|\bms\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'sleep apnea', pattern: /\bsleep\s+apnea\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'hypothyroidism', pattern: /\bhypothyroidism\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'depression', pattern: /\bdepression\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'insomnia', pattern: /\binsomnia\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'lyme disease', pattern: /\blyme\s+disease\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'epstein-barr', pattern: /\bepstein[- ]?barr\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'anxiety', pattern: /\banxiety\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'arthritis', pattern: /\barthritis\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'fibromyalgia', pattern: /\bfibromyalgia\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'psa', pattern: /\bpsa\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'enlarged prostate', pattern: /\benlarged\s+prostate\b/i, category: 'CONDITION', weight: 0.85 },
  { term: 'cardiomyopathy', pattern: /\bcardiomyopathy\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'congestive heart failure', pattern: /\bcongestive\s+heart\s+failure\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'copd', pattern: /\bcopd\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'vertigo', pattern: /\bvertigo\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'stroke', pattern: /\bstroke\b/i, category: 'CONDITION', weight: 0.9 },
  { term: 'blood sugar', pattern: /\b(?:blood\s+sugar|elevated\s+(?:glucose|a1c))\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'a1c', pattern: /\b(?:a1c|hemoglobin\s+a1c)\b/i, category: 'CONDITION', weight: 0.8 },
  { term: 'homocysteine', pattern: /\bhomocysteine\b/i, category: 'CONDITION', weight: 0.75 },
  { term: 'hypercalcemia', pattern: /\bhypercalcemia\b/i, category: 'CONDITION', weight: 0.85 },
];

const PREGNANCY_PATTERNS: KeywordMatch[] = [
  { term: 'pregnant', pattern: /\bpregnant\b/i, category: 'PREGNANCY', weight: 0.95 },
  { term: 'expecting', pattern: /\bexpecting\s+(?:a\s+)?(?:baby|child)/i, category: 'PREGNANCY', weight: 0.9 },
  { term: 'trying to conceive', pattern: /\btrying\s+to\s+(?:conceive|get\s+pregnant)/i, category: 'PREGNANCY', weight: 0.9 },
  { term: 'breastfeeding', pattern: /\bbreastfeed/i, category: 'PREGNANCY', weight: 0.9 },
  { term: 'nursing', pattern: /\bnursing\s+(?:my\s+)?(?:baby|child|infant)/i, category: 'PREGNANCY', weight: 0.85 },
  { term: 'fertility', pattern: /\bfertility\s+treat/i, category: 'PREGNANCY', weight: 0.8 },
  { term: 'IVF', pattern: /\bivf\b/i, category: 'PREGNANCY', weight: 0.85 },
  { term: 'trimester', pattern: /\btrimester\b/i, category: 'PREGNANCY', weight: 0.95 },
  { term: 'due date', pattern: /\bdue\s+date\b/i, category: 'PREGNANCY', weight: 0.85 },
  { term: 'prenatal', pattern: /\bprenatal\b/i, category: 'PREGNANCY', weight: 0.85 },
];

const ALL_PATTERNS: KeywordMatch[] = [
  ...MEDICATION_PATTERNS,
  ...ALLERGY_PATTERNS,
  ...CONDITION_PATTERNS,
  ...PREGNANCY_PATTERNS,
];

// ── Metrics State ──

let _metrics: ChatDisclosureMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Service ──

export class ChatDisclosureService {
  private dedup: DeduplicationState;

  constructor() {
    this.dedup = {
      emittedTypes: new Set(),
      emittedKeys: new Set(),
    };
  }

  /**
   * Scan a patient message for disclosures.
   * Returns detected disclosures (may be empty).
   */
  scan(message: string, turnNumber: number): DetectedDisclosure[] {
    const startTime = Date.now();
    _metrics.totalCalls++;

    const disclosures: DetectedDisclosure[] = [];
    const detectedByType = new Map<DisclosureType, DetectedDisclosure>();

    for (const kw of ALL_PATTERNS) {
      if (kw.pattern.test(message)) {
        const existing = detectedByType.get(kw.category);
        if (existing) {
          existing.matchedKeywords.push(kw.term);
          existing.confidence = Math.min(CONFIDENCE_MAX, Math.max(existing.confidence, CONFIDENCE_MIN + (kw.weight * 0.1)));
        } else {
          detectedByType.set(kw.category, {
            type: kw.category,
            rawText: message,
            matchedKeywords: [kw.term],
            confidence: CONFIDENCE_MIN + (kw.weight * 0.1),
            turnNumber,
            detectedAt: new Date(),
          });
        }
      }
    }

    for (const disclosure of detectedByType.values()) {
      // Clamp confidence to 0.5-0.6 range per PRO.09-C12 contract
      disclosure.confidence = Math.min(CONFIDENCE_MAX, Math.max(CONFIDENCE_MIN, disclosure.confidence));
      disclosures.push(disclosure);
    }

    _successCount++;
    _totalLatency += Date.now() - startTime;
    return disclosures;
  }

  /**
   * Convert detected disclosures to LateRevealEvents.
   * Applies session-scoped deduplication.
   * Multi-type rule: one event per disclosure type.
   */
  toEvents(disclosures: DetectedDisclosure[], sessionId: string): LateRevealEvent[] {
    const events: LateRevealEvent[] = [];

    for (const disclosure of disclosures) {
      // Session-scoped dedup by type + normalized keywords
      const dedupKey = `${disclosure.type}:${disclosure.matchedKeywords.sort().join(',')}`;
      if (this.dedup.emittedKeys.has(dedupKey)) continue;

      const event: LateRevealEvent = {
        eventId: generateEventId(),
        sessionId,
        type: disclosure.type,
        confidence: disclosure.confidence,
        rawText: disclosure.rawText,
        extractedData: buildExtractedData(disclosure),
        detectedAt: disclosure.detectedAt.toISOString(),
        detectionModelVersion: DETECTION_MODEL_VERSION,
        source: EVENT_SOURCE,
      };

      events.push(event);
      this.dedup.emittedKeys.add(dedupKey);
      this.dedup.emittedTypes.add(disclosure.type);
    }

    return events;
  }

  /**
   * Build SafetyDataProfile merge entries from disclosures.
   * These merge into chatMedications, chatAllergies, etc.
   * They do NOT flip DataCompletenessFlags (LOCKED contract).
   */
  toSafetyDataMerges(disclosures: DetectedDisclosure[]): SafetyDataMerge[] {
    const merges: SafetyDataMerge[] = [];

    for (const disclosure of disclosures) {
      const field = disclosureTypeToField(disclosure.type);
      if (field) {
        merges.push({
          field,
          source: 'chat',
          value: disclosure.matchedKeywords,
          turnNumber: disclosure.turnNumber,
          mergedAt: new Date(),
        });
      }
    }

    return merges;
  }

  /**
   * Check if a disclosure type has already been emitted this session.
   */
  hasEmitted(type: DisclosureType): boolean {
    return this.dedup.emittedTypes.has(type);
  }

  /**
   * Reset deduplication state (for testing).
   */
  resetDedup(): void {
    this.dedup.emittedTypes.clear();
    this.dedup.emittedKeys.clear();
  }
}

// ── Helpers ──

function buildExtractedData(disclosure: DetectedDisclosure): LateRevealExtractedData {
  const data: LateRevealExtractedData = {};

  switch (disclosure.type) {
    case 'MEDICATION':
      data.medications = disclosure.matchedKeywords.filter((k) => !['taking', 'prescribed', 'medication', 'thyroid medication', 'cholesterol medication', 'blood pressure medication', 'statin', 'beta blocker', 'iron', 'peptides', 'vitamin pack', 'vitamin supplementation', 'vitamin regimen', 'supplement'].includes(k));
      break;
    case 'ALLERGY':
      data.allergies = disclosure.matchedKeywords.filter((k) => !['allergic to', 'allergy', 'react to', 'sensitive to', "can't take", 'intolerant'].includes(k));
      break;
    case 'CONDITION':
      data.conditions = disclosure.matchedKeywords.filter((k) => !['diagnosed with'].includes(k));
      break;
    case 'PREGNANCY':
      // PRO.09 contract: isPregnant at top level of extractedData (not nested)
      data.isPregnant = disclosure.matchedKeywords.some((k) =>
        ['pregnant', 'expecting', 'trimester', 'due date', 'prenatal'].includes(k),
      );
      break;
  }

  return data;
}

function disclosureTypeToField(type: DisclosureType): string | null {
  switch (type) {
    case 'MEDICATION': return 'chatMedications';
    case 'ALLERGY': return 'chatAllergies';
    case 'CONDITION': return 'chatConditions';
    case 'PREGNANCY': return 'pregnancyStatus';
    default: return null;
  }
}

function generateEventId(): string {
  return `lre-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ── Factory ──

export function createChatDisclosureService(): ChatDisclosureService {
  return new ChatDisclosureService();
}

// ── Metrics ──

export function getMetrics(): ChatDisclosureMetrics {
  return {
    ..._metrics,
    successRate: _metrics.totalCalls > 0 ? _successCount / _metrics.totalCalls : 1,
    avgLatencyMs: _metrics.totalCalls > 0 ? _totalLatency / _metrics.totalCalls : 0,
  };
}
